import { ReactNode, useState, useCallback, useMemo, isValidElement } from 'react';
import { css } from '@emotion/react';
import DialogActions from '@mui/material/DialogActions';
import MuiDialogContent from '@mui/material/DialogContent';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import { AppWindow, XCircle } from '@phosphor-icons/react';
import { Button } from 'client/components/Button';
import { ListItemFigureProps, ListItemFigure } from 'client/components/Other/ListItemFigure';
import { ListItemText } from 'client/components/Other/ListItemText';

export interface DialogContentProps {
  id?: string;
  title: string | JSX.Element;
  description?: string | JSX.Element | null;
  icon?: JSX.Element | null;
  image?: ListItemFigureProps['image'];
  iconOrnament?: JSX.Element | null;
  primaryAction?: string | JSX.Element | null;
  primaryActionIcon?: JSX.Element | null;
  secondaryAction?: string | JSX.Element | null;
  disablePrimary?: boolean;
  hideHeader?: boolean;

  /** Show a close icon next to title */
  dismissable?: boolean;

  /** Return true if dialog should be closed, false if it should stay open. */
  onPrimary?: () => boolean | Promise<boolean>;
  /** Return true if dialog should be closed, false if it should stay open. */
  onSecondary?: () => boolean | Promise<boolean>;
  onClose?: () => void;

  /** Override default actions. Will disable `onPrimary` & `onSecondary`. */
  actions?: JSX.Element | null;
  headerActions?: JSX.Element | null;

  children?: ReactNode;
}

export const DialogContent = ({
  id,
  title,
  description,
  icon = <AppWindow />,
  image,
  iconOrnament = null,
  primaryAction,
  primaryActionIcon = null,
  secondaryAction,
  disablePrimary = false,
  hideHeader = false,
  dismissable = true,
  actions,
  headerActions = null,
  onPrimary,
  onSecondary,
  onClose,
  children,
  ...other
}: DialogContentProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = useCallback(() => !!onClose && onClose(), [onClose]);

  const handlePrimaryClick = useCallback(async () => {
    setIsLoading(true);

    const shouldClose = !onPrimary || (await onPrimary());

    setIsLoading(false);

    if (shouldClose) {
      return handleClose();
    }
  }, [onPrimary, handleClose]);

  const handleSecondaryClick = useCallback(async () => {
    setIsLoading(true);

    const shouldClose = !onSecondary || (await onSecondary());

    setIsLoading(false);

    if (shouldClose) {
      return handleClose();
    }
  }, [onSecondary, handleClose]);

  const dialogActions = useMemo(() => {
    if (actions === null) return null;
    if (!!actions && isValidElement(actions)) return actions;

    let primaryActionComponent: JSX.Element | null = (
      <Button
        primary
        autoFocus
        icon={primaryActionIcon || undefined}
        loading={isLoading}
        disabled={disablePrimary}
        onClick={handlePrimaryClick}
      >
        {primaryAction || 'OK'}
      </Button>
    );
    if (primaryAction === null) primaryActionComponent = null;

    let secondaryActionComponent: JSX.Element | null = <Button onClick={handlePrimaryClick}>{secondaryAction || 'Cancel'}</Button>;
    if (secondaryAction === null) secondaryActionComponent = null;

    if (!primaryActionComponent && !secondaryActionComponent) return null;

    return (
      <>
        {secondaryActionComponent}
        {primaryActionComponent}
      </>
    );
  }, [primaryAction, secondaryAction, disablePrimary, handlePrimaryClick, handleSecondaryClick, actions, isLoading]);

  return (
    <>
      {!hideHeader && (
        <ListItem css={dialogTitleStyle}>
          <ListItemFigure
            icon={icon ? <icon.type size={32} {...icon.props} /> : null}
            image={image}
            ornament={iconOrnament}
            css={dialogFigureStyle}
          />
          <ListItemText
            primary={title}
            secondary={description}
            primaryProps={{ id: id ? `${id}.title` : undefined, variant: 'h6', component: 'h1' }}
            secondaryProps={{ id: id ? `${id}.description` : undefined, component: 'p' }}
          />
          <ListItemSecondaryAction css={dialogCloseActionStyle}>
            {headerActions}
            {!!dismissable && <Button icon={<XCircle size={24} weight="fill" />} onClick={handleClose} />}
          </ListItemSecondaryAction>
        </ListItem>
      )}
      <MuiDialogContent {...other}>{children}</MuiDialogContent>
      {!!dialogActions && <DialogActions css={dialogActionsStyle}>{dialogActions}</DialogActions>}
    </>
  );
};

const dialogTitleStyle = css`
  padding: 16px 24px 0 24px;

  > figure {
    margin-right: 16px;
  }

  > div > p {
    margin-top: -4px;
  }
`;

const dialogFigureStyle = css`
  margin-right: 16px;
`;

const dialogCloseActionStyle = css`
  display: flex;
  gap: 8px;

  > button,
  > a > button {
    background: transparent;
    opacity: 0.7;

    &:hover {
      background: transparent;
      box-shadow: none;
      opacity: 1;
    }
  }
`;

const dialogActionsStyle = css`
  padding: 24px;
`;
